import React, { useContext } from "react";
import { GetScore } from "../../state/GetScore";
import { CenteredColumn, CenteredRow } from "../common/LayoutElements";
import { Spectrum } from "../common/Spectrum";
import { Button } from "../common/Button";
import {
  GameType,
  Team,
  InitialGameState,
  TeamName,
  TeamReverse,
} from "../../state/GameState";
import { GameModelContext } from "../../state/GameModelContext";
import { NewRound } from "../../state/NewRound";
import { Info } from "../common/Info";

import { Trans, useTranslation } from "react-i18next";

export function ViewScore() {
  const { t } = useTranslation();
  const { gameState, clueGiver, spectrumCard } = useContext(GameModelContext);

  if (!clueGiver) {
    return null;
  }

  let score = GetScore(gameState.spectrumTarget, gameState.guess);
  let bonusCoopTurn = false;
  if (gameState.gameType === GameType.Cooperative && score === 4) {
    score = 3;
    bonusCoopTurn = true;
  }

  const wasCounterGuessCorrect =
    (gameState.counterGuess === "left" &&
      gameState.spectrumTarget < gameState.guess) ||
    (gameState.counterGuess === "right" &&
      gameState.spectrumTarget > gameState.guess) ||
    (gameState.counterGuess === "exact" &&
      gameState.spectrumTarget === gameState.guess);

  return (
    <div>
      <Spectrum
        spectrumCard={spectrumCard}
        handleValue={gameState.guess}
        targetValue={gameState.spectrumTarget}
      />
      <CenteredColumn>
        <div style={{ marginBottom: '1rem' }}>
          {t("viewscore.player_clue", { givername: clueGiver.name })}:{" "}
          <strong>{gameState.clue}</strong>
        </div>
        <div style={{ marginBottom: '.5rem' }}>
          {t("viewscore.score")}: <strong>{score}</strong> {t("viewscore.points")}!
          {score === 4 && ` - ${t("viewscore.right_on_target")}`}
          {score === 3 && ` - ${t("viewscore.one_away")}`}
          {score === 2 && ` - ${t("viewscore.two_away")}`}
        </div>
        {gameState.gameType === GameType.Teams && (
          <div style={{ marginBottom: '1rem' }}>
            {TeamName(TeamReverse(clueGiver.team), t)} {t("viewscore.got")}{" "}
            {wasCounterGuessCorrect
              ? (gameState.counterGuess === "exact"
                ? t("viewscore.2_point_exact_guess")
                : t("viewscore.1_point_correct_guess"))
              : t("viewscore.0_point_wrong_guess")}
          </div>
        )}
        {bonusCoopTurn && (
          <Trans
            i18nKey={t("viewscore.bonus_turn") as string}
            components={{
              strong: <strong />,
            }}
          />
        )}
        <NextTurnOrEndGame />
      </CenteredColumn>
    </div>
  );
}

function NextTurnOrEndGame() {
  const { t, i18n } = useTranslation();
  const cardsTranslation = useTranslation("spectrum-cards");
  const { gameState, localPlayer, clueGiver, setGameState } =
    useContext(GameModelContext);

  if (!clueGiver) {
    return null;
  }

  const resetButton = (
    <Button
      text={t("viewscore.reset_game")}
      onClick={() => {
        setGameState({
          ...InitialGameState(i18n.language),
          deckSeed: gameState.deckSeed,
          deckIndex: gameState.deckIndex,
        });
      }}
    />
  );

  // Check if both teams have completed their rounds
  const gameIsOver = gameState.turnsTaken >= (gameState.numberOfRounds * 2) - 1;

  if (gameIsOver) {
    const winner = gameState.leftScore > gameState.rightScore ? Team.Left : 
                  gameState.rightScore > gameState.leftScore ? Team.Right : null;
    
    if (winner) {
      return (
        <>
          <div>
            {t("viewscore.winning_team", { winnerteam: TeamName(winner, t) })}
          </div>
          {resetButton}
        </>
      );
    } else {
      return (
        <>
          <div>{t("viewscore.game_finished")}</div>
          <div>{t("viewscore.tie_game")}</div>
          {resetButton}
        </>
      );
    }
  }

  if (
    gameState.gameType === GameType.Cooperative &&
    gameState.turnsTaken >= 7 + gameState.coopBonusTurns
  ) {
    return (
      <>
        <div>{t("viewscore.game_finished")}</div>
        <div>
          {t("viewscore.final_score_team")}:{" "}
          <strong>
            {gameState.coopScore} {t("viewscore.points")}
          </strong>
        </div>
        {resetButton}
      </>
    );
  }

  const score = GetScore(gameState.spectrumTarget, gameState.guess);

  const scoringTeamString = TeamName(clueGiver.team, t);

  // let bonusTurn = false;

  // if (gameState.gameType === GameType.Teams && score === 4) {
  //   if (
  //     gameState.leftScore < gameState.rightScore &&
  //     clueGiver.team === Team.Left
  //   ) {
  //     bonusTurn = true;
  //   }
  //   if (
  //     gameState.rightScore < gameState.leftScore &&
  //     clueGiver.team === Team.Right
  //   ) {
  //     bonusTurn = true;
  //   }
  // }

  const eligibleToDraw = (() => {
    if (clueGiver.id === localPlayer.id) {
      return false;
    }

    if (gameState.gameType !== GameType.Teams) {
      return true;
    }

    // Get all players in the next team
    const nextTeam = TeamReverse(gameState.clueGiverTeam);
    const teamPlayers = Object.entries(gameState.players)
      .filter(([_, player]) => player.team === nextTeam)
      .map(([id, _]) => id);

    // Check if the player is in the next team
    if (localPlayer.team !== nextTeam) {
      return false;
    }

    // Get the last player from the next team
    const lastPlayer = nextTeam === Team.Left ? gameState.lastLeftTeamPlayer : gameState.lastRightTeamPlayer;
    
    // Find the index of the last player in the team
    const lastIndex = teamPlayers.indexOf(lastPlayer);
    
    // If last player not found or no last player, start from beginning
    const nextIndex = lastIndex === -1 ? 0 : (lastIndex + 1) % teamPlayers.length;
    const nextPlayerId = teamPlayers[nextIndex];

    return localPlayer.id === nextPlayerId;
  })();

  return (
    <>
      {/* {bonusTurn && (
        <CenteredRow>
          <div>
            {t("viewscore.catching_up", { scoringteam: scoringTeamString })}
          </div>
          <Info>{t("viewscore.catching_up_info") as string}</Info>
        </CenteredRow>
      )} */}
      {eligibleToDraw && (
        <Button
          text={t("viewscore.draw_next_card")}
          onClick={() =>
            setGameState(
              NewRound(localPlayer.id, gameState, cardsTranslation.t)
            )
          }
        />
      )}
    </>
  );
}
